// MslPob.jsx
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "../pages/CustomButton";
import XLSX from "xlsx-js-style";
import Loader from "../dist/Loader";
import formatDate from "../utils/formatDate";

const MslPob = () => {
  const [states, setStates] = useState([{ id: "All", name: "All" }]);
  const [divisions, setDivisions] = useState([{ id: "All", name: "All" }]);
  const [employees, setEmployees] = useState([
    { id: "All", first_name: "All", last_name: "-" },
  ]);
  const [selectedState, setSelectedState] = useState("All");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedEmployee, setSelectedEmployee] = useState("All");
  const [retailerType, setRetailerType] = useState("All");
  const [activeStatus, setActiveStatus] = useState("All");
  const [summary, setSummary] = useState(null);
  const now = new Date();
  const currentMonthIso = `${now.getFullYear().toString().padStart(4, "0")}-${(
    now.getMonth() + 1
  )
    .toString()
    .padStart(2, "0")}`;

  const [startMonth, setStartMonth] = useState(currentMonthIso);
  const [endMonth, setEndMonth] = useState(currentMonthIso);

  const getDefaultFinancialYear = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    if (month >= 4) {
      return `${year}-${year + 1}`;
    } else {
      return `${year - 1}-${year}`;
    }
  };
  const [financialYear, setFinancialYear] = useState(getDefaultFinancialYear());
  const [tableData, setTableData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const getFinancialYearRange = (fy) => {
    const [startYear, endYear] = fy.split("-").map(Number);
    return {
      start: `${startYear}-04`,
      end: `${endYear}-03`,
    };
  };

  const getMonthsInRange = (start, end) => {
    if (!start || !end) return [];
    const result = [];
    let current = new Date(start + "-01");
    const endDate = new Date(end + "-01");
    while (current <= endDate) {
      const key = current
        .toLocaleString("en-US", { month: "short" })
        .toLowerCase();
      const display = current.toLocaleString("en-US", { month: "short" });
      result.push({ key, label: display });
      current.setMonth(current.getMonth() + 1);
    }
    return result;
  };

  const filteredMonths = getMonthsInRange(startMonth, endMonth);

  // ─── FIXED: fetchStates ───────────────────────────────────────────────────
  const fetchStates = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/states`, {
        headers: getAuthHeaders(),
      });
      const data = Array.isArray(res.data) ? res.data : [];
      setStates([{ id: "All", name: "All" }, ...data]);
      setSelectedState((prev) => {
        if (
          !data.find((s) => String(s.id) === String(prev)) &&
          prev !== "All"
        ) {
          return "All";
        }
        return prev;
      });
    } catch (err) {
      toast.error("Failed to load states");
      setStates([{ id: "All", name: "All" }]);
      setSelectedState("All");
    }
  }, []);

  // ─── FIXED: fetchDivisions — now RETURNS fresh data for chaining ──────────
  const fetchDivisions = useCallback(async (stateId = "All") => {
    try {
      let url = `${BASE_URL}/api/divisions`;
      if (stateId && stateId !== "All")
        url += `?state_id=${encodeURIComponent(stateId)}`;
      const res = await axios.get(url, { headers: getAuthHeaders() });
      const data = Array.isArray(res.data) ? res.data : [];
      setDivisions([{ id: "All", name: "All" }, ...data]);
      setSelectedDivision((prev) => {
        if (
          !data.find((d) => String(d.id) === String(prev)) &&
          prev !== "All"
        ) {
          return "All";
        }
        return prev;
      });
      return data; // ← FIXED: return so callers can chain employees with fresh list
    } catch (err) {
      toast.error("Failed to load divisions");
      setDivisions([{ id: "All", name: "All" }]);
      setSelectedDivision("All");
      return [];
    }
  }, []);

  // ─── FIXED: fetchEmployees — accepts currentDivisions param ──────────────
  const fetchEmployees = useCallback(
    async (stateId = "All", divisionId = "All", currentDivisions = []) => {
      try {
        setEmployees([{ id: "All", first_name: "All", last_name: "-" }]);
        setSelectedEmployee("All");

        const params = {};
        if (stateId && stateId !== "All") params.state_id = stateId;

        // FIXED: use passed-in currentDivisions instead of stale closure
        if (divisionId && divisionId !== "All") {
          const division = currentDivisions.find(
            (d) => String(d.id) === String(divisionId),
          );
          if (division?.name) params.division = division.name;
        }

        const res = await axios.get(`${BASE_URL}/api/employees`, {
          params: Object.keys(params).length ? params : undefined,
          headers: getAuthHeaders(),
        });
        const data = Array.isArray(res.data) ? res.data : [];
        setEmployees([
          { id: "All", first_name: "All", last_name: "-" },
          ...data,
        ]);
        setSelectedEmployee((prev) => {
          if (
            !data.find((e) => String(e.id) === String(prev)) &&
            prev !== "All"
          ) {
            return "All";
          }
          return prev;
        });
      } catch (err) {
        toast.error("Failed to load employees");
        setEmployees([{ id: "All", first_name: "All", last_name: "-" }]);
        setSelectedEmployee("All");
      }
    },
    [],
  );

  // ─── FIXED: Initial load — chain divisions → employees properly ───────────
  useEffect(() => {
    fetchStates();
    fetchDivisions("All").then((divs) => {
      fetchEmployees("All", "All", divs);
    });
  }, [fetchStates, fetchDivisions, fetchEmployees]);

  // ─── FIXED: On state change — await divisions before fetching employees ───
  useEffect(() => {
    const load = async () => {
      setSelectedDivision("All");
      const divs = await fetchDivisions(selectedState || "All");
      fetchEmployees(selectedState || "All", "All", divs);
    };
    load();
  }, [selectedState, fetchDivisions, fetchEmployees]);

  // ─── FIXED: On division change — pass live divisions state ────────────────
  useEffect(() => {
    const load = async () => {
      setSelectedDivision("All");
      const divs = await fetchDivisions(selectedState || "All");
      await fetchEmployees(selectedState || "All", "All", divs);
    };
    load();
  }, [selectedState]); // ← intentionally only selectedState, no fetchDivisions/fetchEmployees


  // ─── ADD this new useEffect for division changes only ────────────────────────
  useEffect(() => {
    if (selectedDivision === undefined) return;
    // Use a ref snapshot of divisions to avoid the loop
    fetchEmployees(selectedState || "All", selectedDivision || "All", divisions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDivision]);

  const formatMonthForAPI = (monthString) => {
    if (!monthString) return "-";
    const [year, month] = monthString.split("-");
    if (!year || !month) return "-";
    const date = new Date(year, Number(month) - 1);
    return date.toLocaleString("default", { month: "long" }) + " " + year;
  };

  const toNumber = (val) => {
    const n = Number(val);
    return isNaN(n) || !isFinite(n) ? 0 : n;
  };

  // REPLACE the existing totals reducer with this:
  const totals = (tableData || []).reduce(
    (acc, item) => {
      acc.total_visits += toNumber(item.total_visit_fy);
      acc.total_pob_fy += toNumber(item.total_pob_fy);
      acc.avg_pob_prev_fy += toNumber(item.avg_pob_prev_fy); // ← ADD
      acc.avg_pob_curr_fy += toNumber(item.avg_pob_curr_fy); // ← ADD
      filteredMonths.forEach((m) => {
        acc[`${m.key}_visit`] =
          (acc[`${m.key}_visit`] || 0) + toNumber(item[`${m.key}_visit`]);
        acc[`${m.key}_pob`] =
          (acc[`${m.key}_pob`] || 0) + toNumber(item[`${m.key}_pob`]);
      });
      return acc;
    },
    {
      total_visits: 0,
      total_pob_fy: 0,
      avg_pob_prev_fy: 0,
      avg_pob_curr_fy: 0,
    },
  );

  const getFinancialYears = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;

    // Current FY start year
    const currentFYStart = month >= 4 ? year : year - 1;

    const years = [];

    // Start from FY 2018-2019
    for (let y = 2018; y <= currentFYStart + 1; y++) {
      years.push(`${y}-${y + 1}`);
    }

    return years;
  };

  const fyRange = getFinancialYearRange(financialYear);
  const todayIso = currentMonthIso;
  const maxMonth = todayIso > fyRange.end ? fyRange.end : todayIso;

  const buildApiParams = () => {
    const params = {};
    if (selectedEmployee && selectedEmployee !== "All")
      params.employee_id = selectedEmployee;
    if (retailerType && retailerType !== "All")
      params.retailer_type = retailerType.toLowerCase();
    if (activeStatus && activeStatus !== "All")
      params.active = activeStatus === "Yes";
    if (selectedState && selectedState !== "All")
      params.state_id = selectedState;
    if (selectedDivision && selectedDivision !== "All") {
      const division = divisions.find(
        (d) => String(d.id) === String(selectedDivision),
      );
      if (division && division.name) params.division = division.name;
    }
    const formattedStart = formatMonthForAPI(startMonth);
    const formattedEnd = formatMonthForAPI(endMonth);
    if (formattedStart) params.start_month = formattedStart;
    if (formattedEnd) params.end_month = formattedEnd;
    if (financialYear) params.financial_year = financialYear;
    return params;
  };

  // const handleGoClick = async () => {
  //   try {
  //     setShowTable(false);
  //     setIsLoading(true);
  //     const params = buildApiParams();
  //     const response = await axios.get(
  //       `${BASE_URL}/api/combined-retailers?is_manager=true`,
  //       {
  //         params: Object.keys(params).length ? params : undefined,
  //         headers: getAuthHeaders(),
  //       },
  //     );
  //   const data = (
  //     Array.isArray(response.data)
  //       ? response.data
  //       : Array.isArray(response.data?.data)
  //         ? response.data.data
  //         : []
  //   ).slice(0, -1);
  //     setTableData(data);
  //     setShowTable(true);
  //   } catch (err) {
  //     toast.error("Failed to fetch report data");
  //     setTableData([]);
  //     setShowTable(true);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };
  const handleGoClick = async () => {
    try {
      setShowTable(false);
      setIsLoading(true);

      const params = buildApiParams();

      const response = await axios.get(
        `${BASE_URL}/api/combined-retailers?is_manager=true`,
        {
          params,
          headers: getAuthHeaders(),
        }
      );

      const raw = response?.data;
      let retailerData = [];
      let summaryData = null;

      if (Array.isArray(raw)) {
        // Format B: flat array
        const lastItem = raw[raw.length - 1];
        if (lastItem && lastItem.summary && !lastItem.type && !lastItem.id) {
          // Last element is the summary wrapper — extract it
          summaryData = lastItem.summary;
          retailerData = raw.slice(0, raw.length - 1);
        } else {
          // No summary wrapper found — treat all elements as data
          retailerData = raw;
          summaryData = null;
        }
      } else if (raw && typeof raw === "object") {
        // Format A: { data, summary }
        retailerData = Array.isArray(raw.data) ? raw.data : [];
        summaryData = raw.summary || null;
      }

      setTableData(retailerData);
      setSummary(summaryData);
      setShowTable(true);

    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch report data");
      setTableData([]);
      setSummary(null);
      setShowTable(true);
    } finally {
      setIsLoading(false);
    }
  };
  const handleExportExcel = () => {
    if ((!tableData || tableData.length === 0) && !summary) {
      toast.info("No data to export");
      return;
    }

    try {
      const wb = XLSX.utils.book_new();
      const ws = {};

      const mainHeaderStyle = {
        font: { bold: true, color: { rgb: "FFFFFFFF" }, sz: 11 },
        fill: { fgColor: { rgb: "FF2563EB" } },
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
        border: {
          top: { style: "thin", color: { rgb: "FF000000" } },
          bottom: { style: "thin", color: { rgb: "FF000000" } },
          left: { style: "thin", color: { rgb: "FF000000" } },
          right: { style: "thin", color: { rgb: "FF000000" } },
        },
      };

      const rowStyle = {
        font: { sz: 10 },
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
        border: {
          top: { style: "thin", color: { rgb: "FFBFBFBF" } },
          bottom: { style: "thin", color: { rgb: "FFBFBFBF" } },
          left: { style: "thin", color: { rgb: "FFBFBFBF" } },
          right: { style: "thin", color: { rgb: "FFBFBFBF" } },
        },
      };

      const setCell = (r, c, v, s) => {
        ws[XLSX.utils.encode_cell({ r, c })] = {
          v: v ?? "-",
          t: typeof v === "number" ? "n" : "s",
          s,
        };
      };

      // ✅ NEW: real Excel date cell
      const setDateCell = (r, c, rawVal, s) => {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (rawVal) {
          try {
            const date = new Date(
              /^\d{4}-\d{2}-\d{2}$/.test(String(rawVal))
                ? `${rawVal}T00:00:00`
                : rawVal
            );
            if (!isNaN(date.getTime())) {
              ws[addr] = { v: date, t: "d", z: "dd-mmm-yyyy", s };
              return;
            }
          } catch { }
        }
        ws[addr] = { v: "", t: "s", s };
      };

      const mainHeaders = [
        "S.No", "Employee ID", "Employee Name", "Type", "Code", "Retailer",
        "Active", "State", "Sub State", "City", "Area", "Route", "Mobile",
        "Address", "System Added Date", "Latest Visit Date",
        "Prev FY Avg", "Curr FY Avg", "FY Total Visits", "FY Total POB",
        ...filteredMonths.flatMap((m) => [`${m.label} Visits`, `${m.label} POB`]),
      ];
      const totalDataCols = mainHeaders.length;

      mainHeaders.forEach((header, c) => setCell(0, c, header, mainHeaderStyle));

      (tableData || []).forEach((item, index) => {
        const r = index + 1;
        const excelRow = r + 1;

        const rowData = [
          index + 1, item.user_id || "-", item.employee_name || "-",
          item.type || "-", item.code || "-", item.name || "-",
          item.active ? "Yes" : "No", item.state_name || "-", item.substate || "-",
          item.city_name || "-", item.area_name || "-", item.route_name || "-",
          item.mobile || "-", item.address || "-",
          null, null, // ✅ col 14 & 15 — patched below as real Excel dates
          Number(item.avg_pob_prev_fy || 0), Number(item.avg_pob_curr_fy || 0),
          0, 0,
        ];
        filteredMonths.forEach((m) => {
          rowData.push(Number(item[`${m.key}_visit`] || 0));
          rowData.push(Number(item[`${m.key}_pob`] || 0));
        });

        // ✅ Skip col 14 & 15 — handled by setDateCell
        rowData.forEach((value, c) => {
          if (c === 14 || c === 15) return;
          setCell(r, c, value, rowStyle);
        });

        // ✅ Patch date cols as real Excel dates
        setDateCell(r, 14, item.system_added_date, rowStyle);
        setDateCell(r, 15, item.latest_visit_date, rowStyle);

        const firstMonthCol = 20;
        const lastMonthCol = 19 + filteredMonths.length * 2;

        const visitCols = [];
        for (let c = firstMonthCol; c <= lastMonthCol; c += 2)
          visitCols.push(`${XLSX.utils.encode_col(c)}${excelRow}`);
        ws[XLSX.utils.encode_cell({ r, c: 18 })] = { f: visitCols.join("+"), t: "n", s: rowStyle };

        const pobCols = [];
        for (let c = firstMonthCol + 1; c <= lastMonthCol; c += 2)
          pobCols.push(`${XLSX.utils.encode_col(c)}${excelRow}`);
        ws[XLSX.utils.encode_cell({ r, c: 19 })] = { f: pobCols.join("+"), t: "n", s: rowStyle };
      });

      const dataRowCount = (tableData || []).length;
      const grandTotalRowIdx = dataRowCount + 1;

      setCell(grandTotalRowIdx, 0, "GRAND TOTAL", mainHeaderStyle);
      for (let c = 1; c <= 15; c++) setCell(grandTotalRowIdx, c, "", mainHeaderStyle);
      [16, 17, 18, 19].forEach((c) => {
        const col = XLSX.utils.encode_col(c);
        ws[XLSX.utils.encode_cell({ r: grandTotalRowIdx, c })] = {
          f: `SUM(${col}2:${col}${dataRowCount + 1})`,
          t: "n",
          s: mainHeaderStyle,
        };
      });
      let dynCol = 20;
      filteredMonths.forEach(() => {
        [dynCol, dynCol + 1].forEach((c) => {
          const col = XLSX.utils.encode_col(c);
          ws[XLSX.utils.encode_cell({ r: grandTotalRowIdx, c })] = {
            f: `SUM(${col}2:${col}${dataRowCount + 1})`,
            t: "n",
            s: mainHeaderStyle,
          };
        });
        dynCol += 2;
      });

      let nextRow = grandTotalRowIdx + 1;

      if (
        summary &&
        summary.pob_range &&
        summary.calls &&
        summary.pob &&
        summary.sob &&
        summary.visits
      ) {
        const months = filteredMonths.map((m) => m.key);
        const monthLabels = filteredMonths.map((m) => m.label.toUpperCase());
        const summaryCols = months.length + 1;
        const colOffset = Math.floor((totalDataCols - summaryCols) / 2);

        const summaryConfigs = [
          {
            title: "📦  POB Range / Retailer Count",
            headerLabel: "Range",
            titleBg: "FF1D4ED8",
            subBg: "FFEFF6FF",
            subFont: "FF1E40AF",
            footerBg: "FF1D4ED8",
            altBg: "FFF0F4FF",
            rows: Object.entries(summary.pob_range),
            totalLogic: (m) =>
              Object.values(summary.pob_range).reduce(
                (sum, row) => sum + Number(row?.[m] || 0), 0
              ),
          },
          {
            title: "📈  Calls & POB",
            headerLabel: "Metric",
            titleBg: "FF059669",
            subBg: "FFECFDF5",
            subFont: "FF065F46",
            footerBg: "FF059669",
            altBg: "FFF0FDF4",
            rows: [
              ["Total Calls", summary.calls.total_calls],
              ["Call Avg Per Day", summary.calls.avg_calls_per_day],
              ["POB Avg Per Day", summary.pob.avg_pob_per_day],
              ["Total POB", summary.pob.total_pob],
              ["Total SOB", summary.sob.total_sob],
            ],
            totalLogic: (m) =>
              Number(summary.calls.total_calls?.[m] || 0) +
              Number(summary.pob.total_pob?.[m] || 0) +
              Number(summary.sob.total_sob?.[m] || 0),
          },
          {
            title: "🏃  Visits",
            headerLabel: "Visits",
            titleBg: "FF7C3AED",
            subBg: "FFF5F3FF",
            subFont: "FF4C1D95",
            footerBg: "FF7C3AED",
            altBg: "FFFAF5FF",
            rows: Object.entries(summary.visits).map(([k, v]) => [
              k === "6+" ? "6 Above" : k, v,
            ]),
            totalLogic: (m) =>
              Object.values(summary.visits).reduce(
                (sum, row) => sum + Number(row?.[m] || 0), 0
              ),
          },
        ];

        if (!ws["!merges"]) ws["!merges"] = [];
        if (!ws["!rows"]) ws["!rows"] = [];

        summaryConfigs.forEach((cfg) => {
          nextRow += 2;

          const titleStyle = {
            font: { bold: true, color: { rgb: "FFFFFFFF" }, sz: 13 },
            fill: { fgColor: { rgb: cfg.titleBg } },
            alignment: { horizontal: "center", vertical: "center", wrapText: false },
            border: {
              top: { style: "medium", color: { rgb: "FF000000" } },
              bottom: { style: "medium", color: { rgb: "FF000000" } },
              left: { style: "medium", color: { rgb: "FF000000" } },
              right: { style: "medium", color: { rgb: "FF000000" } },
            },
          };
          for (let c = colOffset; c < colOffset + summaryCols; c++) {
            setCell(nextRow, c, c === colOffset ? cfg.title : "", titleStyle);
          }
          ws["!merges"].push({
            s: { r: nextRow, c: colOffset },
            e: { r: nextRow, c: colOffset + summaryCols - 1 },
          });
          ws["!rows"][nextRow] = { hpt: 28 };
          nextRow++;

          const subHeaderLabelStyle = {
            font: { bold: true, color: { rgb: cfg.subFont }, sz: 11 },
            fill: { fgColor: { rgb: cfg.subBg } },
            alignment: { horizontal: "left", vertical: "center" },
            border: {
              top: { style: "thin", color: { rgb: "FF000000" } },
              bottom: { style: "thin", color: { rgb: "FF000000" } },
              left: { style: "medium", color: { rgb: "FF000000" } },
              right: { style: "thin", color: { rgb: "FFBFBFBF" } },
            },
          };
          const subHeaderMonthStyle = {
            font: { bold: true, color: { rgb: cfg.subFont }, sz: 11 },
            fill: { fgColor: { rgb: cfg.subBg } },
            alignment: { horizontal: "center", vertical: "center" },
            border: {
              top: { style: "thin", color: { rgb: "FF000000" } },
              bottom: { style: "thin", color: { rgb: "FF000000" } },
              left: { style: "thin", color: { rgb: "FFBFBFBF" } },
              right: { style: "thin", color: { rgb: "FFBFBFBF" } },
            },
          };
          setCell(nextRow, colOffset, cfg.headerLabel, subHeaderLabelStyle);
          monthLabels.forEach((ml, ci) =>
            setCell(nextRow, colOffset + ci + 1, ml, subHeaderMonthStyle)
          );
          ws["!rows"][nextRow] = { hpt: 20 };
          nextRow++;

          cfg.rows.forEach(([label, data], ri) => {
            const dataBg = ri % 2 === 1 ? cfg.altBg : "FFFFFFFF";
            const labelCellStyle = {
              font: { bold: true, color: { rgb: "FF1E293B" }, sz: 10 },
              fill: { fgColor: { rgb: dataBg } },
              alignment: { horizontal: "left", vertical: "center" },
              border: {
                top: { style: "thin", color: { rgb: "FFBFBFBF" } },
                bottom: { style: "thin", color: { rgb: "FFBFBFBF" } },
                left: { style: "medium", color: { rgb: "FF000000" } },
                right: { style: "thin", color: { rgb: "FFBFBFBF" } },
              },
            };
            const dataCellStyle = {
              font: { sz: 10, color: { rgb: "FF334155" } },
              fill: { fgColor: { rgb: dataBg } },
              alignment: { horizontal: "center", vertical: "center" },
              border: {
                top: { style: "thin", color: { rgb: "FFBFBFBF" } },
                bottom: { style: "thin", color: { rgb: "FFBFBFBF" } },
                left: { style: "thin", color: { rgb: "FFBFBFBF" } },
                right: { style: "thin", color: { rgb: "FFBFBFBF" } },
              },
            };
            setCell(nextRow, colOffset, label, labelCellStyle);
            months.forEach((m, ci) =>
              setCell(nextRow, colOffset + ci + 1, Number(data?.[m] || 0), dataCellStyle)
            );
            ws["!rows"][nextRow] = { hpt: 18 };
            nextRow++;
          });

          const footerLabelStyle = {
            font: { bold: true, color: { rgb: "FFFFFFFF" }, sz: 11 },
            fill: { fgColor: { rgb: cfg.footerBg } },
            alignment: { horizontal: "left", vertical: "center" },
            border: {
              top: { style: "medium", color: { rgb: "FF000000" } },
              bottom: { style: "medium", color: { rgb: "FF000000" } },
              left: { style: "medium", color: { rgb: "FF000000" } },
              right: { style: "thin", color: { rgb: "FFBFBFBF" } },
            },
          };
          const footerDataStyle = {
            font: { bold: true, color: { rgb: "FFFFFFFF" }, sz: 11 },
            fill: { fgColor: { rgb: cfg.footerBg } },
            alignment: { horizontal: "center", vertical: "center" },
            border: {
              top: { style: "medium", color: { rgb: "FF000000" } },
              bottom: { style: "medium", color: { rgb: "FF000000" } },
              left: { style: "thin", color: { rgb: "FFBFBFBF" } },
              right: { style: "thin", color: { rgb: "FFBFBFBF" } },
            },
          };
          setCell(nextRow, colOffset, "TOTAL", footerLabelStyle);
          months.forEach((m, ci) =>
            setCell(nextRow, colOffset + ci + 1, cfg.totalLogic(m), footerDataStyle)
          );
          ws["!rows"][nextRow] = { hpt: 22 };
          nextRow++;
        });
      }

      ws["!ref"] = XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: nextRow, c: totalDataCols - 1 },
      });

      ws["!cols"] = [
        { wch: 8 }, { wch: 16 }, { wch: 26 }, { wch: 12 }, { wch: 14 }, { wch: 30 },
        { wch: 10 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 20 },
        { wch: 16 }, { wch: 36 }, { wch: 20 }, { wch: 20 }, { wch: 14 }, { wch: 14 },
        { wch: 18 }, { wch: 16 },
        ...Array.from({ length: filteredMonths.length * 2 }, () => ({ wch: 15 })),
      ];

      ws["!freeze"] = { xSplit: 0, ySplit: 1 };
      ws["!autofilter"] = { ref: `A1:${XLSX.utils.encode_col(totalDataCols - 1)}1` };

      XLSX.utils.book_append_sheet(wb, ws, "MSL Report");

      const sanitize = (s) => String(s).replace(/[^a-z0-9_-]/gi, "_");
      const fileName = `MSL_Report_${sanitize(selectedState || "All")}_${sanitize(selectedEmployee || "All")}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, fileName, { compression: true });

    } catch (err) {
      console.error(err);
      toast.error("Failed to export Excel");
    }
  };
  const handlePrint = () => {
    const headerCols = [
      "S.No", "Employee ID", "Employee Name", "Type", "Code", "Retailer",
      "Active", "State", "Sub State", "City", "Area", "Route", "MOBILE",
      "Address", "System Added Date", "Latest Visit Date",
      "Prev FY Avg", "Curr FY Avg", "FY Total Visits", "FY Total POB",
      ...filteredMonths.flatMap((m) => [`${m.label} Visits`, `${m.label} POB`]),
    ];

    const htmlRows =
      tableData && tableData.length > 0
        ? tableData.map((item, idx) => {
          const monthCols = filteredMonths.map((m) =>
            `<td style="text-align:right;padding:6px;border:1px solid #ccc">${item[`${m.key}_visit`] ?? 0}</td>
              <td style="text-align:right;padding:6px;border:1px solid #ccc">${item[`${m.key}_pob`] ?? 0}</td>`
          ).join("");
          return `<tr>
              <td style="padding:6px;border:1px solid #ccc">${idx + 1}</td>
              <td style="padding:6px;border:1px solid #ccc">${item.user_id || "-"}</td>
              <td style="padding:6px;border:1px solid #ccc">${item.employee_name || "-"}</td>
              <td style="padding:6px;border:1px solid #ccc">${item.type || "-"}</td>
              <td style="padding:6px;border:1px solid #ccc">${item.code || "-"}</td>
              <td style="padding:6px;border:1px solid #ccc">${item.name || "-"}</td>
              <td style="padding:6px;border:1px solid #ccc">${item.active ? "Yes" : "No"}</td>
              <td style="padding:6px;border:1px solid #ccc">${item.state_name || "-"}</td>
              <td style="padding:6px;border:1px solid #ccc">${item.substate || "-"}</td>
              <td style="padding:6px;border:1px solid #ccc">${item.city_name || "-"}</td>
              <td style="padding:6px;border:1px solid #ccc">${item.area_name || "-"}</td>
              <td style="padding:6px;border:1px solid #ccc">${item.route_name || "-"}</td>
              <td style="padding:6px;border:1px solid #ccc">${item.mobile || "-"}</td>
              <td style="padding:6px;border:1px solid #ccc">${(item.address || "-").replace(/</g, "&lt;")}</td>
              <td style="padding:6px;border:1px solid #ccc">${formatDate(item.system_added_date)}</td>
              <td style="padding:6px;border:1px solid #ccc">${formatDate(item.latest_visit_date)}</td>
              <td style="padding:6px;border:1px solid #ccc">${item.avg_pob_prev_fy ?? "-"}</td>
              <td style="padding:6px;border:1px solid #ccc">${item.avg_pob_curr_fy ?? "-"}</td>
              <td style="padding:6px;border:1px solid #ccc;text-align:right">${item.total_visit_fy ?? 0}</td>
              <td style="padding:6px;border:1px solid #ccc;text-align:right">${item.total_pob_fy ?? 0}</td>
              ${monthCols}
            </tr>`;
        }).join("")
        : `<tr><td colspan="${20 + filteredMonths.length * 2}" style="text-align:center;padding:12px;border:1px solid #ccc">No records found.</td></tr>`;

    const grandTotalMonthCols = filteredMonths.map((m) =>
      `<td style="padding:6px;border:1px solid #ccc;text-align:right;background:#2563eb;color:#fff;font-weight:bold">${totals[`${m.key}_visit`] || 0}</td>
      <td style="padding:6px;border:1px solid #ccc;text-align:right;background:#2563eb;color:#fff;font-weight:bold">${totals[`${m.key}_pob`] || 0}</td>`
    ).join("");

    const grandTotalRow = `
      <tr>
        <td colspan="16" style="padding:6px;border:1px solid #ccc;text-align:right;background:#2563eb;color:#fff;font-weight:bold">GRAND TOTAL</td>
        <td style="padding:6px;border:1px solid #ccc;text-align:right;background:#2563eb;color:#fff;font-weight:bold">${Number(totals.avg_pob_prev_fy || 0).toLocaleString("en-IN")}</td>
        <td style="padding:6px;border:1px solid #ccc;text-align:right;background:#2563eb;color:#fff;font-weight:bold">${Number(totals.avg_pob_curr_fy || 0).toLocaleString("en-IN")}</td>
        <td style="padding:6px;border:1px solid #ccc;text-align:right;background:#2563eb;color:#fff;font-weight:bold">${Number(totals.total_visits || 0).toLocaleString("en-IN")}</td>
        <td style="padding:6px;border:1px solid #ccc;text-align:right;background:#2563eb;color:#fff;font-weight:bold">${Number(totals.total_pob_fy || 0).toLocaleString("en-IN")}</td>
        ${grandTotalMonthCols}
      </tr>`;

    // Build summary HTML (only if summary exists)
    let summaryHtml = "";
    if (summary && summary.pob_range && summary.calls && summary.pob && summary.sob && summary.visits) {
      const months = filteredMonths.map(m => m.key);
      const monthLabels = filteredMonths.map(m => m.label.toUpperCase());

      const buildSummaryTable = (title, headerLabel, rows, isCalls = false) => {
        const bodyRows = rows.map(([label, data], i) =>
          `<tr style="background:${i % 2 ? "#f9fafb" : "#fff"}">
            <td style="padding:6px;border:1px solid #ccc">${label}</td>
            ${months.map(m => `<td style="padding:6px;border:1px solid #ccc;text-align:center">${Number(data?.[m] || 0).toLocaleString("en-IN")}</td>`).join("")}
          </tr>`
        ).join("");

        const totalCells = months.map(m => {
          const total = isCalls
            ? Number(summary.calls.total_calls?.[m] || 0) + Number(summary.pob.total_pob?.[m] || 0) + Number(summary.sob.total_sob?.[m] || 0)
            : rows.reduce((sum, [, data]) => sum + Number(data?.[m] || 0), 0);
          return `<td style="padding:6px;border:1px solid #ccc;text-align:center;background:#1d4ed8;color:#fff;font-weight:bold">${total.toLocaleString("en-IN")}</td>`;
        }).join("");

        return `
          <div style="margin-bottom:24px;">
            <div style="background:#1d4ed8;color:#fff;font-weight:700;font-size:12px;padding:7px 12px;">${title}</div>
            <table style="width:100%;border-collapse:collapse;font-size:11px;">
              <thead>
                <tr style="background:#eff6ff;">
                  <th style="padding:6px;border:1px solid #ccc;text-align:left">${headerLabel}</th>
                  ${monthLabels.map(m => `<th style="padding:6px;border:1px solid #ccc">${m}</th>`).join("")}
                </tr>
              </thead>
              <tbody>${bodyRows}</tbody>
              <tfoot>
                <tr>
                  <td style="padding:6px;border:1px solid #ccc;background:#1d4ed8;color:#fff;font-weight:bold">TOTAL</td>
                  ${totalCells}
                </tr>
              </tfoot>
            </table>
          </div>`;
      };

      summaryHtml = `
        <div style="page-break-before:always;margin-top:32px;">
          <h2 style="text-align:center">MSL Summary Report</h2>
          ${buildSummaryTable("📦 POB Range / Retailer Count", "Range", Object.entries(summary.pob_range))}
          ${buildSummaryTable("📈 Calls & POB", "Metric", [
        ["Total Calls", summary.calls.total_calls],
        ["Call Avg Per Day", summary.calls.avg_calls_per_day],
        ["POB Avg Per Day", summary.pob.avg_pob_per_day],
        ["Total POB", summary.pob.total_pob],
        ["Total SOB", summary.sob.total_sob],
      ], true)}
          ${buildSummaryTable("🏃 Visits", "Visits",
        Object.entries(summary.visits).map(([k, v]) => [k === "6+" ? "6 Above" : k, v])
      )}
        </div>`;
    }

    const filtersSummary = `Filters: State=${selectedState || "All"}, Division=${selectedDivision || "All"}, Employee=${selectedEmployee || "All"}, RetailerType=${retailerType}, Active=${activeStatus}, Period=${formatMonthForAPI(startMonth) || "-"} to ${formatMonthForAPI(endMonth) || "N/A"}`;

    const printHtml = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>MSL Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 16px; color: #111; }
            h2 { margin-top: 0; text-align: center; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #ccc; padding: 6px; vertical-align: top; }
            thead th { background: #f2f2f2; }
            .meta { margin-bottom: 8px; font-size: 13px; }
            @media print {
              thead { display: table-header-group; }
              tr { page-break-inside: avoid; }
              @page { size: landscape; }
            }
          </style>
        </head>
        <body>
          <h2>MSL Report</h2>
          <div class="meta">${filtersSummary}</div>
          <table>
            <thead>
              <tr>${headerCols.map((c) => `<th style="padding:6px;text-align:left">${c}</th>`).join("")}</tr>
            </thead>
            <tbody>${htmlRows}</tbody>
            <tfoot>${grandTotalRow}</tfoot>
          </table>
          ${summaryHtml}
          <div style="margin-top:12px;font-size:12px;text-align:center">Generated on: ${new Date().toLocaleString()}</div>
        </body>
      </html>`;

    const w = window.open("-", "_blank", "width=1200,height=800");
    if (!w) { toast.error("Please allow popups to print"); return; }
    w.document.open();
    w.document.write(printHtml);
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch (e) { toast.error("Printing failed"); } }, 500);
  };

  const handlePrintsummary = () => {
    if (!summary) {
      toast.info("No summary to print");
      return;
    }

    const months = filteredMonths.map(m => m.key);
    const monthLabels = filteredMonths.map(m => m.label.toUpperCase());

    const buildTable = (title, headerLabel, rows) => `
      <div style="margin-bottom:24px;">
        <div style="background:#1d4ed8;color:#fff;font-weight:700;font-size:12px;padding:7px 12px;">
          ${title}
        </div>

        <table style="width:100%;border-collapse:collapse;font-size:11px;">
          <thead>
            <tr style="background:#eff6ff;">
              <th style="padding:6px;border:1px solid #ccc;text-align:left;">${headerLabel}</th>
              ${monthLabels.map(m => `<th style="padding:6px;border:1px solid #ccc;">${m}</th>`).join("")}
            </tr>
          </thead>

          <tbody>
            ${rows.map(([label, data], i) => `
              <tr style="background:${i % 2 ? "#f9fafb" : "#fff"};">
                <td style="padding:6px;border:1px solid #ccc;">${label}</td>
                ${months.map(m => `
                  <td style="padding:6px;border:1px solid #ccc;text-align:center;">
                    ${Number(data?.[m] || 0).toLocaleString("en-IN")}
                  </td>
                `).join("")}
              </tr>
            `).join("")}
          </tbody>

          <tfoot>
            <tr>
              <td style="background:#1d4ed8;color:#fff;font-weight:bold;padding:6px;">TOTAL</td>
              ${months.map(m => {
      let total = 0;

      if (title.includes("Calls & POB")) {
        total =
          Number(summary.calls.total_calls?.[m] || 0) +
          Number(summary.pob.total_pob?.[m] || 0) +
          Number(summary.sob.total_sob?.[m] || 0);
      } else {
        total = rows.reduce((sum, [, data]) => sum + Number(data?.[m] || 0), 0);
      }

      return `<td style="background:#1d4ed8;color:#fff;font-weight:bold;text-align:center;">
                  ${total.toLocaleString("en-IN")}
                </td>`;
    }).join("")}
            </tr>
          </tfoot>
        </table>
      </div>
    `;

    const pobRangeRows = Object.entries(summary.pob_range);
    const callsPobRows = [
      ["Total Calls", summary.calls.total_calls],
      ["Call Avg Per Day", summary.calls.avg_calls_per_day],
      ["POB Avg Per Day", summary.pob.avg_pob_per_day],
      ["Total POB", summary.pob.total_pob],
      ["Total SOB", summary.sob.total_sob],
    ];
    const visitsRows = Object.entries(summary.visits).map(([k, v]) => [
      k === "6+" ? "6 Above" : k,
      v
    ]);

    const html = `
      <html>
      <head>
        <title>Summary Report</title>
        <style>
          body { font-family: Arial; padding: 20px; }
          h2 { text-align: center; }
        </style>
      </head>
      <body>
        <h2>MSL Summary Report</h2>

        ${buildTable("📦 POB Range", "Range", pobRangeRows)}
        ${buildTable("📈 Calls & POB", "Metric", callsPobRows)}
        ${buildTable("🏃 Visits", "Visits", visitsRows)}

      </body>
      </html>
    `;

    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
    w.print();
  };
  const handleExportExcelsummary = () => {
    if (!summary) {
      toast.info("No summary data to export");
      return;
    }

    try {
      const wb = XLSX.utils.book_new();
      const months = filteredMonths.map(m => m.key);
      const monthLabels = filteredMonths.map(m => m.label.toUpperCase());

      // ===== STYLES (UNCHANGED) =====
      const hStyle = {
        font: { bold: true, color: { rgb: "FFFFFFFF" }, sz: 11 },
        fill: { fgColor: { rgb: "FF1D4ED8" } },
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
        border: {
          top: { style: "thin", color: { rgb: "FF000000" } },
          bottom: { style: "thin", color: { rgb: "FF000000" } },
          left: { style: "thin", color: { rgb: "FF000000" } },
          right: { style: "thin", color: { rgb: "FF000000" } },
        },
      };

      const subHStyle = {
        ...hStyle,
        fill: { fgColor: { rgb: "FFEFF6FF" } },
        font: { bold: true, color: { rgb: "FF1E40AF" }, sz: 10 },
      };

      const labelStyle = {
        font: { bold: true, sz: 10, color: { rgb: "FF334155" } },
        alignment: { horizontal: "left", vertical: "center" },
      };

      const dataStyle = {
        font: { sz: 10, color: { rgb: "FF475569" } },
        alignment: { horizontal: "center", vertical: "center" },
      };

      const setCell = (ws, r, c, v, s) => {
        const addr = XLSX.utils.encode_cell({ r, c });
        ws[addr] = { v: v ?? "-", t: typeof v === "number" ? "n" : "s", s };
      };

      // ===== MAIN BUILDER =====
      const buildSheet = (sectionTitle, headerLabel, rows) => {
        const ws = {};
        const totalCols = months.length + 1;

        // Title
        setCell(ws, 0, 0, sectionTitle, hStyle);
        for (let c = 1; c < totalCols; c++) setCell(ws, 0, c, "", hStyle);

        // Header row
        setCell(ws, 1, 0, headerLabel, subHStyle);
        monthLabels.forEach((ml, ci) =>
          setCell(ws, 1, ci + 1, ml, subHStyle)
        );

        // Data rows
        rows.forEach(([label, data], ri) => {
          setCell(ws, ri + 2, 0, label, labelStyle);

          months.forEach((m, ci) => {
            setCell(
              ws,
              ri + 2,
              ci + 1,
              Number(data?.[m] || 0),
              dataStyle
            );
          });
        });

        // ===== TOTAL ROW (FIXED) =====
        const totalRow = rows.length + 2;
        setCell(ws, totalRow, 0, "TOTAL", hStyle);

        months.forEach((m, ci) => {
          let total = 0;

          // ✅ SPECIAL LOGIC (MATCH UI)
          if (sectionTitle.includes("Calls & POB")) {
            total =
              Number(summary.calls.total_calls?.[m] || 0) +
              Number(summary.pob.total_pob?.[m] || 0) +
              Number(summary.sob.total_sob?.[m] || 0);
          } else {
            total = rows.reduce((sum, [, data]) => {
              return sum + Number(data?.[m] || 0);
            }, 0);
          }

          setCell(ws, totalRow, ci + 1, total, hStyle);
        });

        ws["!ref"] = XLSX.utils.encode_range({
          s: { r: 0, c: 0 },
          e: { r: totalRow, c: totalCols - 1 },
        });

        ws["!cols"] = [{ wch: 28 }, ...months.map(() => ({ wch: 14 }))];

        ws["!merges"] = [
          { s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 1 } },
        ];

        ws["!freeze"] = { xSplit: 1, ySplit: 2 };

        return ws;
      };

      // ===== DATA =====
      const pobRangeRows = Object.entries(summary.pob_range);

      const callsPobRows = [
        ["Total Calls", summary.calls.total_calls],
        ["Call Avg Per Day", summary.calls.avg_calls_per_day],
        ["POB Avg Per Day", summary.pob.avg_pob_per_day],
        ["Total POB", summary.pob.total_pob],
        ["Total SOB", summary.sob.total_sob],
      ];

      const visitsRows = Object.entries(summary.visits).map(([k, v]) => [
        k === "6+" ? "6 Above" : k,
        v,
      ]);

      // ===== SHEETS =====
      XLSX.utils.book_append_sheet(
        wb,
        buildSheet("POB Range / Retailer Count", "Range", pobRangeRows),
        "POB Range"
      );

      XLSX.utils.book_append_sheet(
        wb,
        buildSheet("Calls & POB", "Month", callsPobRows),
        "Calls & POB"
      );

      XLSX.utils.book_append_sheet(
        wb,
        buildSheet("Visits", "Visits", visitsRows),
        "Visits"
      );

      // ===== DOWNLOAD =====
      const fileName = `MSL_Summary_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;

      XLSX.writeFile(wb, fileName, { compression: true });

    } catch (err) {
      console.error(err);
      toast.error("Failed to export Summary Excel");
    }
  };
  const totalColumns = 20 + filteredMonths.length * 2;
  return (
    <div className="p-2 border-2 border-gray-300 rounded-lg bg-white">
      {isLoading && <Loader onCancel={() => setIsLoading(false)} />}
      <div className="w-full p-4 border border-gray-300 rounded-lg bg-white">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700">MSL </span>
          <span className="text-blue-500">Report</span>
        </h1>

        {/* ROW 1 */}
        <div className="grid grid-cols-5 gap-3 items-center mb-3">
          <label className="text-right text-xs font-normal">State</label>
          <select
            value={selectedState}
            onChange={(e) => setSelectedState(e.target.value)}
            className="col-span-1 w-full border border-gray-300 rounded px-1 py-[2px] text-xs"
          >
            {states.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <label className="text-right text-xs font-normal">Division</label>
          <select
            value={selectedDivision}
            onChange={(e) => setSelectedDivision(e.target.value)}
            className="col-span-1 w-full border border-gray-300 rounded px-1 py-[2px] text-xs"
          >
            {divisions.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <div></div>
        </div>

        {/* ROW 2 */}
        <div className="grid grid-cols-5 gap-3 items-center mb-3">
          <label className="text-right text-xs">Employee</label>
          <select
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
            className="w-full border border-gray-300 rounded px-1 py-[2px] text-xs"
          >
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.id === "All"
                  ? "All"
                  : `${emp.first_name || "-"} ${emp.last_name || "-"} ${emp.employee || "-"}
                  | ${emp.designation || "-"} 
                  | ${emp.division || "-"} 
                  | ${emp.state_name || emp.state || "-"}`}
              </option>
            ))}
          </select>
          <label className="text-right text-xs">Retailer Type</label>
          <select
            value={retailerType}
            onChange={(e) => setRetailerType(e.target.value)}
            className="w-full border border-gray-300 rounded px-1 py-[2px] text-xs"
          >
            <option>All</option>
            <option value={"chemist"}>Retailer</option>
            <option>Doctor</option>
          </select>
          <div></div>
        </div>

        {/* ROW 3 */}
        <div className="grid grid-cols-5 gap-3 items-center mb-3">
          <label className="text-right text-xs">FY Start Month</label>
          <input
            type="month"
            value={startMonth}
            min={fyRange.start}
            max={maxMonth}
            onChange={(e) => setStartMonth(e.target.value)}
            className="w-[250px] border border-gray-300 rounded px-1 py-[2px] text-xs"
          />
          <label className="text-right text-xs">FY End Month</label>
          <input
            type="month"
            value={endMonth}
            min={fyRange.start}
            max={maxMonth}
            onChange={(e) => setEndMonth(e.target.value)}
            className="w-[250px] border border-gray-300 rounded px-1 py-[2px] text-xs"
          />
          <div></div>
        </div>

        {/* ROW 4 */}
        <div className="grid grid-cols-5 gap-3 items-start mb-3">
          <label className="text-right text-xs">Financial Year</label>
          <select
            value={financialYear}
            onChange={(e) => setFinancialYear(e.target.value)}
            className="w-full border border-gray-300 rounded px-1 py-[2px] text-xs"
          >
            {getFinancialYears().map((fy) => (
              <option key={fy}>{fy}</option>
            ))}
          </select>
          <label className="text-right text-xs">Active</label>
          <div className="flex flex-col gap-1">
            <select
              value={activeStatus}
              onChange={(e) => setActiveStatus(e.target.value)}
              className="w-full border border-gray-300 rounded px-1 py-[2px] text-xs"
            >
              <option>All</option>
              <option>Yes</option>
              <option>No</option>
            </select>
          </div>
          <div></div>
        </div>

        {/* GO BUTTON */}
        <div className="flex justify-center mt-4">
          <CustomButton
            onClick={handleGoClick}
            disabled={isLoading}
            variant="go"
          >
            GO
          </CustomButton>
        </div>
      </div>

      {showTable && (
        <>
          {/* FIXED: overflow-x-auto + w-full on wrapper */}
          <div className="border rounded shadow table-wrapper mt-5">
            <table className="unlock-dcr-leave-table text-sm">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th>S.No</th>
                  <th>Employee ID</th>
                  <th>Employee Name</th>
                  <th>Type</th>
                  <th>Code</th>
                  <th>Retailer</th>
                  <th>Active</th>
                  <th>State</th>
                  <th>Sub State</th>
                  <th>City</th>
                  <th>Area</th>
                  <th>Route</th>
                  <th>MOBILE</th>
                  <th>Address</th>
                  <th>System Added Date</th>
                  <th>Latest Visit Date</th>
                  <th>Prev FY Avg</th>
                  <th>Curr FY Avg</th>
                  <th>FY Total Visits</th>
                  <th>FY Total POB</th>
                  {filteredMonths.map((m) => (
                    <React.Fragment key={m.key}>
                      <th>{m.label} Visits</th>
                      <th>{m.label} POB</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(!tableData || tableData.length === 0) && (
                  <tr>
                    <td
                      colSpan={totalColumns}
                      style={{ textAlign: "center", padding: 12 }}
                    >
                      No records found for the selected filters.
                    </td>
                  </tr>
                )}
                {(tableData || []).map((item, index) => (
                  <tr key={item.code || index}>
                    <td>{index + 1}</td>
                    <td>{item.user_id || "-"}</td>
                    <td>{item.employee_name || "-"}</td>
                    <td>{item.type || "-"}</td>
                    <td>{item.code || "-"}</td>
                    <td>{item.name || "-"}</td>
                    <td>{item.active ? "Yes" : "No"}</td>
                    <td>{item.state_name || "-"}</td>
                    <td>{item.substate || "-"}</td>
                    <td>{item.city_name || "-"}</td>
                    <td>{item.area_name || "-"}</td>
                    <td className="fixed-address">{item.route_name || "-"}</td>
                    <td>{item.mobile || "-"}</td>
                    <td className="fixed-address">{item.address || "-"}</td>
                    <td>{formatDate(item.system_added_date)}</td>
                    <td>{formatDate(item.latest_visit_date)}</td>
                    <td>{item.avg_pob_prev_fy ?? "-"}</td>
                    <td>{item.avg_pob_curr_fy ?? "-"}</td>
                    <td>{item.total_visit_fy ?? 0}</td>
                    <td>{item.total_pob_fy ?? 0}</td>
                    {filteredMonths.map((m) => (
                      <React.Fragment key={m.key}>
                        <td>{item[`${m.key}_visit`] ?? 0}</td>
                        <td>{item[`${m.key}_pob`] ?? 0}</td>
                      </React.Fragment>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {/* FIXED: colSpan 20 (was 18) */}
                <tr
                  style={{
                    backgroundColor: "#2563eb",
                    color: "#fff",
                    fontWeight: "bold",
                  }}
                >
                  <td
                    colSpan={16}
                    style={{ textAlign: "right", padding: "6px" }}
                  >
                    GRAND TOTAL
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {Number(totals.avg_pob_prev_fy || 0).toLocaleString(
                      "en-IN",
                    )}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {Number(totals.avg_pob_curr_fy || 0).toLocaleString(
                      "en-IN",
                    )}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {Number(totals.total_visits || 0).toLocaleString("en-IN")}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {Number(totals.total_pob_fy || 0).toLocaleString("en-IN")}
                  </td>
                  {filteredMonths.map((m) => (
                    <React.Fragment key={m.key}>
                      <td style={{ textAlign: "right" }}>
                        {totals[`${m.key}_visit`] || 0}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {totals[`${m.key}_pob`] || 0}
                      </td>
                    </React.Fragment>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>


        </>
      )}


      {summary &&
        summary.pob_range &&
        summary.calls &&
        summary.pob &&
        summary.sob &&
        summary.visits && (

          <div className="mt-6 max-w-5xl mx-auto space-y-6 text-xs">

            <p className="text-center text-sm font-bold text-blue-700 uppercase">
              Summary Report
            </p>

            {/* ================= POB RANGE ================= */}
            <div className="border rounded shadow-sm overflow-hidden">
              <div className="bg-blue-600 text-white px-3 py-1.5 font-semibold">
                📦 POB Range / Retailer Count
              </div>

              <table className="w-full text-center text-xs">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="text-left px-3 py-2">Range</th>
                    {filteredMonths.map(m => (
                      <th key={m.key}>{m.label.toUpperCase()}</th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {Object.entries(summary.pob_range).map(([k, v], i) => (
                    <tr key={k} className={i % 2 ? "bg-gray-50" : ""}>
                      <td className="text-left px-3 py-2 font-medium">{k}</td>
                      {filteredMonths.map(m => (
                        <td key={m.key}>{v[m.key] ?? "-"}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>

                {/* ✅ FOOTER ONLY */}
                <tfoot className="bg-blue-600 text-white font-bold">
                  <tr>
                    <td className="text-left px-3 py-2">TOTAL</td>
                    {filteredMonths.map(m => {
                      const total = Object.values(summary.pob_range).reduce(
                        (sum, row) => sum + Number(row?.[m.key] || 0),
                        0
                      );
                      return <td key={m.key}>{total}</td>;
                    })}
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* ================= CALLS & POB ================= */}
            <div className="border rounded shadow-sm overflow-hidden">
              <div className="bg-emerald-600 text-white px-3 py-1.5 font-semibold">
                📈 Calls & POB
              </div>

              <table className="w-full text-center text-xs">
                <thead className="bg-emerald-50">
                  <tr>
                    <th className="text-left px-3 py-2">Month</th>
                    {filteredMonths.map(m => (
                      <th key={m.key}>{m.label.toUpperCase()}</th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {[
                    ["Total Calls", summary.calls.total_calls],
                    ["Call Avg Per Day", summary.calls.avg_calls_per_day],
                    ["POB Avg Per Day", summary.pob.avg_pob_per_day],
                    ["Total POB", summary.pob.total_pob],
                    ["Total SOB", summary.sob.total_sob],
                  ].map(([label, data], i) => (
                    <tr key={label} className={i % 2 ? "bg-gray-50" : ""}>
                      <td className="text-left px-3 py-2 font-medium">{label}</td>
                      {filteredMonths.map(m => (
                        <td key={m.key}>
                          {Number(data?.[m.key] || 0).toLocaleString("en-IN")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ================= VISITS (SHIFTED BELOW) ================= */}
            <div className="border rounded shadow-sm overflow-hidden">
              <div className="bg-violet-600 text-white px-3 py-1.5 font-semibold">
                🏃 Visits
              </div>

              <table className="w-full text-center text-xs">
                <thead className="bg-violet-50">
                  <tr>
                    <th className="text-left px-3 py-2">Visits</th>
                    {filteredMonths.map(m => (
                      <th key={m.key}>{m.label.toUpperCase()}</th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {Object.entries(summary.visits).map(([k, v], i) => (
                    <tr key={k} className={i % 2 ? "bg-gray-50" : ""}>
                      <td className="text-left px-3 py-2">{k === "6+" ? "6 Above" : k}</td>
                      {filteredMonths.map(m => (
                        <td key={m.key}>{v[m.key] ?? 0}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>

                {/* ✅ FOOTER ONLY */}
                <tfoot className="bg-violet-600 text-white font-bold">
                  <tr>
                    <td className="text-left px-3 py-2">TOTAL</td>
                    {filteredMonths.map(m => {
                      const total = Object.values(summary.visits).reduce(
                        (sum, row) => sum + Number(row?.[m.key] || 0),
                        0
                      );
                      return <td key={m.key}>{total}</td>;
                    })}
                  </tr>
                </tfoot>
              </table>
              {/* ===== ACTION BUTTONS ===== */}
              {/* Remove the old two-button block and replace with: */}
              <div className="mt-4 flex gap-4 justify-center">
                <CustomButton onClick={handlePrint} variant="print" disabled={isLoading}>
                  🖨 Print
                </CustomButton>
                <CustomButton onClick={handleExportExcel} variant="export" disabled={isLoading}>
                  📥 Export
                </CustomButton>
              </div>
            </div>

          </div>
        )}
    </div>
  );
};

export default MslPob;